import { z } from 'zod';

import { DraftStatusSchema } from '../enums.js';

import { IsoDateTimeSchema, UuidSchema } from './common.js';

// alert-service drafts (spec §5.4). One open draft per (owner, recipient, assembly).

export const DraftVersionSchema = z.object({
  id: UuidSchema,
  versionNo: z.number().int().positive(),
  subject: z.string(),
  body: z.string(),
  createdAt: IsoDateTimeSchema,
});
export type DraftVersion = z.infer<typeof DraftVersionSchema>;

export const DraftSchema = z.object({
  id: UuidSchema,
  status: DraftStatusSchema,
  from: z.email(),
  to: z.email(),
  assemblyId: UuidSchema,
  lastError: z.string().nullable(),
  currentVersion: DraftVersionSchema,
  alerts: z.array(z.object({ alertId: UuidSchema, code: z.string(), lineStatus: z.string() })),
});
export type Draft = z.infer<typeof DraftSchema>;

export const DraftIdParamsSchema = z.object({ id: UuidSchema });
export const DraftAlertParamsSchema = z.object({ id: UuidSchema, alertId: UuidSchema });

/** Any change to subject or body creates a new version. */
export const UpdateDraftRequestSchema = z
  .object({
    /** The version the edit started from; a stale base is refused (BR-030). */
    baseVersionId: UuidSchema,
    subject: z.string().trim().min(1).max(300).optional(),
    body: z.string().min(1).max(20_000).optional(),
  })
  .refine((v) => v.subject !== undefined || v.body !== undefined, {
    message: 'Change the subject or the body.',
  });
export type UpdateDraftRequest = z.infer<typeof UpdateDraftRequestSchema>;

/** BR-030: approval applies to exactly this version. */
export const ApproveDraftRequestSchema = z.object({ versionId: UuidSchema });
export type ApproveDraftRequest = z.infer<typeof ApproveDraftRequestSchema>;

export const DraftResponseSchema = z.object({ draft: DraftSchema });
export type DraftResponse = z.infer<typeof DraftResponseSchema>;
